import bundleConfig, { mainLineProperty } from '@theme/bundle-gift-config';

/**
 * Writes the Kaching main-line property into product-card add-to-cart forms.
 *
 * Product cards post through Horizon's own `<product-form-component>`, which builds its body with
 * `new FormData(form)` (assets/product-form.js). Anything inside the `<form>` is therefore sent, so
 * a hidden `properties[__kaching_bundles]` input is enough to make the line bundle-aware at the
 * moment it is created — no interception of `window.fetch` required.
 *
 * That matters: Kraken and Kaching each already wrap `window.fetch`, and a third wrapper would only
 * reach Kaching's interceptor if it happened to install last. This approach has no ordering
 * dependency and cannot break either app's request handling.
 *
 * This is an optimisation, not the correctness mechanism. It removes the round trip in which
 * assets/bundle-gift-reconciler.js would otherwise have to stamp the line after the fact, so the
 * gift shows up in the drawer immediately instead of a beat later. The reconciler still covers every
 * path this cannot reach — Kraken's own drawer upsells, "Gleich mitbestellen", and any card rendered
 * by app markup rather than a theme form.
 *
 * Note: Kraken rewrites the outgoing add into its own JSON shape. If it turns out to drop line
 * properties while doing so, this module simply has no effect and the reconciler handles it.
 */

const BUNDLE_PROPERTY = '__kaching_bundles';
const PROPERTY_INPUT_NAME = `properties[${BUNDLE_PROPERTY}]`;

/** Cards live inside `quick-add-component`; the PDP form and the quick-add modal do not. */
const CARD_FORM_SELECTOR = 'quick-add-component product-form-component[data-product-id]';

/** @type {Map<number, { dealId: string, deal: Record<string, any> }>} Product id → deal. */
const dealsByProductId = new Map(
  Object.entries(bundleConfig.deals ?? {}).map(([dealId, deal]) => [deal.productId, { dealId, deal }])
);

/**
 * A stable group id per deal, so repeated card adds land on one line instead of fragmenting into
 * several groups. The reconciler adopts whichever group id it finds first and unifies the rest.
 *
 * @param {string} dealId
 * @returns {string}
 */
function groupIdFor(dealId) {
  return dealId;
}

/** @param {Element} component */
function enrich(component) {
  const productId = Number(/** @type {HTMLElement} */ (component).dataset.productId);
  const entry = dealsByProductId.get(productId);

  if (!entry) return;

  // A rendered widget means Kaching owns this form's properties — never compete with it.
  if (component.querySelector('kaching-bundle')) return;

  const form = component.querySelector('form');

  if (!form || form.querySelector(`[name="${PROPERTY_INPUT_NAME}"]`)) return;

  const input = document.createElement('input');

  input.type = 'hidden';
  input.name = PROPERTY_INPUT_NAME;
  // Cards always post quantity 1; the reconciler restamps nothing for `bid` because it is cosmetic.
  input.value = mainLineProperty({ dealId: entry.dealId, deal: entry.deal, groupId: groupIdFor(entry.dealId), quantity: 1 });
  form.appendChild(input);
}

/** @param {ParentNode} root */
function enrichWithin(root) {
  for (const component of root.querySelectorAll(CARD_FORM_SELECTOR)) enrich(component);
}

function boot() {
  if (!dealsByProductId.size) return;

  enrichWithin(document);

  // Cards also arrive after load: facet filtering, pagination and section morphs all replace markup.
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches(CARD_FORM_SELECTOR)) enrich(node);
        else enrichWithin(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
